import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import SearchableSelect from '../common/SearchableSelect';
import { Checkbox } from '@mui/material';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import StoreLabelDialog from '../item/StoreLabelDialog';   // Q3
import { SerialService } from '../../services/serial.service';
import { ItemService } from '../../services/item.service';
import { getStoresByInstitutionId } from '../../services/store.service';
import { fetchAllPages } from '../../services/salesOrder.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { humanizeEnum } from '../../utils/format';
import { formatDateTime } from '../../utils/dateUtils';
import { Item } from '../../interfaces/item.interface';
import {
  SERIAL_STATUSES, SerialDetail, SerialPage, SerialStatus,
} from '../../interfaces/inventory/serial.interface';

interface Opt { id: string; name: string; }

const STATUS_COLORS: Record<SerialStatus, 'success' | 'info' | 'warning' | 'default' | 'error'> = {
  IN_STOCK: 'success', ISSUED: 'info', SOLD: 'warning', RETURNED: 'error', REMOVED: 'default',
};

/** Where a history row's source document opens (null: no page, or the caller lacks its view key). */
export const sourcePath = (sourceType?: string | null, sourceId?: string | null): string | null => {
  if (!sourceType || !sourceId) return null;
  switch (sourceType) {
    case 'GOODS_RECEIPT': return `/procurement/goods-receipts/${sourceId}`;
    case 'STOCK_TRANSFER': return `/stock-transfers/${sourceId}`;
    case 'STOCKTAKE': return `/stocktakes/${sourceId}`;
    case 'REQUISITION': return `/requisition/${sourceId}`;
    case 'DELIVERY_CHALLAN': return `/sales/challans/${sourceId}`;
    case 'SALES_RETURN': return `/sales/returns/${sourceId}`;
    default: return null;
  }
};

const SerialHistory: React.FC<{ detail: SerialDetail }> = ({ detail }) => {
  const s = detail.serial;
  return (
    <Paper sx={{ p: 2, mb: 2 }} data-testid="serial-detail" data-help-section="store.serials.history">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 1 }}>
        <Typography variant="h6">{s.serialNo}</Typography>
        <Chip size="small" color={STATUS_COLORS[s.status]} label={humanizeEnum(s.status)} />
      </Box>
      <Typography variant="body2">Item: {s.itemCode ? `${s.itemCode} - ` : ''}{s.itemName ?? '-'}</Typography>
      <Typography variant="body2">Store: {s.storeName ?? '-'}</Typography>
      {detail.customerVisible && (s.status === 'SOLD' || s.status === 'RETURNED') && (
        <Typography variant="body2" data-testid="serial-customer">
          Customer: {detail.customerName ?? '-'}{detail.challanNo ? ` (challan ${detail.challanNo})` : ''}
        </Typography>
      )}
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>History</Typography>
      <TableContainer>
        <Table size="small">
          <TableHead><TableRow sx={{ backgroundColor: '#f5f5f5', '& th': { fontWeight: 'bold' } }}>
            <TableCell data-help="store.serials.hist.col.when">When</TableCell><TableCell data-help="store.serials.hist.col.movement">Movement</TableCell><TableCell data-help="store.serials.hist.col.from">From</TableCell>
            <TableCell data-help="store.serials.hist.col.to">To</TableCell><TableCell data-help="store.serials.hist.col.doc">Document</TableCell><TableCell data-help="store.serials.hist.col.by">By</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {detail.history.map((m, i) => {
              const path = sourcePath(m.sourceType, m.sourceId);
              return (
                <TableRow key={`${m.occurredAt}-${i}`}>
                  <TableCell>{formatDateTime(m.occurredAt)}</TableCell>
                  <TableCell>{humanizeEnum(m.movement)}</TableCell>
                  <TableCell>{m.fromStoreName ?? '-'}</TableCell>
                  <TableCell>{m.toStoreName ?? '-'}</TableCell>
                  <TableCell>
                    {path ? (
                      <Link component={RouterLink} to={path}>
                        {m.sourceNumber ? `${humanizeEnum(m.sourceType)} ${m.sourceNumber}` : humanizeEnum(m.sourceType)}
                      </Link>
                    ) : '-'}
                  </TableCell>
                  <TableCell>{m.byUserName ?? '-'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

/** F6b serial lookup: search serial numbers, open one for its status, store and movement history. */
const SerialLookup: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const institutionId = AuthService.getSelectedInstitution();
  const [q, setQ] = useState('');
  const [itemId, setItemId] = useState('');
  const [storeId, setStoreId] = useState('');
  const [status, setStatus] = useState<SerialStatus | ''>('');
  const [items, setItems] = useState<Item[]>([]);
  const [stores, setStores] = useState<Opt[]>([]);
  const [result, setResult] = useState<SerialPage | null>(null);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<SerialDetail | null>(null);
  // ---- Q3: serial labels ----
  const canManageItems = usePermission(PERMISSIONS.ITEM_MANAGE);
  const canPrintLabels = usePermission(PERMISSIONS.STORE_LABEL_PRINT);
  const canLabel = canManageItems || canPrintLabels;
  const [labelIds, setLabelIds] = useState<string[]>([]);
  const [labelOpen, setLabelOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const s = await fetchAllPages((p, sz) => getStoresByInstitutionId(p, sz, 'ALL'));
        setStores(s.map(x => ({ id: x.id!, name: x.storeName })));
      } catch (e) { toast.showApiError(e, 'Failed to load the store filter'); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const searchItems = useCallback(async (text: string) => {
    try { setItems((await ItemService.getItemsByInstitution(0, 50, text, 'STOCK')).content); } catch { setItems([]); }
  }, []);
  useEffect(() => { searchItems(''); }, [searchItems]);

  const run = async (p = page, sz = size) => {
    try {
      setLoading(true);
      setResult(await SerialService.search(institutionId!, { q: q.trim(), itemId, storeId, status, page: p, size: sz }));
    } catch (e) { toast.showApiError(e, 'Failed to search serial numbers'); } finally { setLoading(false); }
  };
  useEffect(() => { run(0); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id) { setDetail(null); return; }
    (async () => {
      try { setDetail(await SerialService.detail(institutionId!, id)); } catch (e) { setDetail(null); toast.showApiError(e, 'Failed to load the serial number'); }
    })();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ p: 3 }} data-help-page="store.serials.page">
      <Typography variant="h5" sx={{ mb: 2 }}>Serial Numbers</Typography>
      {detail && <SerialHistory detail={detail} />}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }} data-help-section="store.serials.filter">
        <TextField size="small" data-help="store.serials.filter.serial" label="Serial number" value={q} onChange={e => setQ(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { setPage(0); run(0); } }}
          inputProps={{ 'data-testid': 'serial-q' }} sx={{ flex: '1 1 220px', maxWidth: 320 }} />
        <Box sx={{ flex: '1 1 260px', maxWidth: 360 }} data-help="store.serials.filter.item">
          <SearchableSelect<Item> options={items} value={itemId} onChange={v => setItemId(v)}
            getOptionLabel={it => `${it.itemCode} - ${it.itemName}`} label="Item" size="small" placeholder="All items"
            disableClientFilter onInputChange={searchItems} />
        </Box>
        <Box sx={{ flex: '1 1 220px', maxWidth: 320 }} data-help="store.serials.filter.store">
          <SearchableSelect<Opt> options={stores} value={storeId} onChange={v => setStoreId(v)} getOptionLabel={o => o.name}
            label="Store" size="small" placeholder="All stores" />
        </Box>
        <Box sx={{ flex: '1 1 180px', maxWidth: 240 }} data-help="store.serials.filter.status">
          <SearchableSelect<Opt> options={SERIAL_STATUSES.map(s => ({ id: s, name: humanizeEnum(s) }))} value={status}
            onChange={v => setStatus(v as SerialStatus | '')} getOptionLabel={o => o.name} label="Status" size="small"
            placeholder="Any status" />
        </Box>
        <Button data-help="store.serials.search" variant="contained" onClick={() => { setPage(0); run(0); }} disabled={loading}>Search</Button>
        {canLabel && (
          <Button data-help="store.serials.printLabels" variant="outlined" disabled={labelIds.length === 0}
            onClick={() => setLabelOpen(true)}>
            Print labels{labelIds.length > 0 ? ` (${labelIds.length})` : ''}
          </Button>
        )}{/* Q3 */}
      </Box>
      {loading && <CircularProgress />}
      {result && !loading && (
        <Paper data-help-section="store.serials.table">
          {result.content.length === 0 && <Alert severity="info">No serial numbers match.</Alert>}
          {result.content.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow sx={{ backgroundColor: '#f5f5f5', '& th': { fontWeight: 'bold' } }}>
                  {canLabel && <TableCell padding="checkbox" data-help="store.serials.col.select" />}{/* Q3 */}
                  <TableCell data-help="store.serials.col.serial">Serial</TableCell><TableCell data-help="store.serials.col.item">Item</TableCell><TableCell data-help="store.serials.col.store">Store</TableCell>
                  <TableCell data-help="store.serials.col.status">Status</TableCell><TableCell data-help="store.serials.col.last">Last movement</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {result.content.map(r => (
                    <TableRow key={r.id} hover selected={r.id === id} sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/inventory/serials/${r.id}`)}>
                      {canLabel && (
                        <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                          <Checkbox data-help="store.serials.action.select" size="small" checked={labelIds.includes(r.id)}
                            inputProps={{ 'aria-label': `Select serial ${r.serialNo}` }}
                            onChange={() => setLabelIds(prev => prev.includes(r.id) ? prev.filter(x => x !== r.id) : [...prev, r.id])} />
                        </TableCell>
                      )}{/* Q3 */}
                      <TableCell>{r.serialNo}</TableCell>
                      <TableCell>{r.itemCode ? `${r.itemCode} - ` : ''}{r.itemName ?? '-'}</TableCell>
                      <TableCell>{r.storeName ?? '-'}</TableCell>
                      <TableCell><Chip size="small" color={STATUS_COLORS[r.status]} label={humanizeEnum(r.status)} /></TableCell>
                      <TableCell>{r.lastMovementAt ? formatDateTime(r.lastMovementAt) : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination component="div" count={result.totalElements} page={page} rowsPerPage={size}
            rowsPerPageOptions={[25, 50, 100, 200]}
            onPageChange={(_, p) => { setPage(p); run(p); }}
            onRowsPerPageChange={e => { const sz = Number(e.target.value); setSize(sz); setPage(0); run(0, sz); }} />
        </Paper>
      )}
      {labelOpen && <StoreLabelDialog open kind="SERIAL" ids={labelIds} onClose={() => setLabelOpen(false)} />}{/* Q3 */}
    </Box>
  );
};

export default SerialLookup;
